import { Component, Element, Prop, Listen, Watch, h, Event, EventEmitter } from '@stencil/core';
import { generateId, getRequiredInd, isInError } from '../../utils/functions';

/**
 * Radio button groups are used to group multiple radio buttons in a single form field that takes care of the label, help message and error state.
 */
/**
 * @slot label - Label for the radio button group. Alternatively, you can use the label attribute.
 */
@Component({
  tag: 'som-radio-button-group',
  styleUrl: 'som-radio-button-group.scss',
})
export class SomRadioButtonGroup {
  @Element() el: HTMLElement;

  /**
   * This property can be used to set the inline label.
   */
  @Prop() label: string = '';

  /**
   * This property will visually hide the label.  A label is still required to be provided for screenreader support.
   */
  @Prop() hideLabel: boolean = false;

  /**
   * This property is a list of CSS classes that can be used to style the label.
   * @deprecated with version 4.0.1 release
   */
   @Prop() labelClass?: string = '';      

  /**
   * This property can be used to set a help text.
   */
  @Prop() help: string = '';

  /**
   * This property can be used to set an error. If a non-empty string is given, it will be used as the error message.
   */
  @Prop({mutable: true}) error: string | null = null;

  /**
   * This boolean sets whether the form field group is required or not.
   */
  @Prop() required: boolean = false;
  
  /**
  * This property is used to override the required indicator. By default, forms elements will show an `optional` indicator
  * but some forms are better served showing the `required` indicator instead.
  */
   @Prop() requiredInd: 'required' | 'optional' | 'none';      

  /**
   * This boolean sets whether the form field group should display the checkboxes inline or not.
   */
  @Prop() inline: boolean = false;

  /**
   * This property is the radio button value sent on submit when used in a form.
   */
  @Prop({ mutable: true, reflect: true }) value: string = null;

  /**
   * This property is used to set the tooltip text.
   */
  @Prop() tooltip: string | null = null;
  
  /**
   * This event is emitted when the radio button is toggled.
   */
  @Event() somChange: EventEmitter;

  groupId: string = generateId('som-radio-button-group');

  helpElementId: string;

  childrenData: any;

  tooltipID: string = generateId('som-tooltipID-');

  @Watch('value') 
  handleValueChange(newVal) {
    const radios = Array.prototype.slice.call(this.el.querySelectorAll('som-radio-button'));
    radios.forEach(radio => {
      if (newVal === radio.value) {
        radio.setAttribute('checked', true);
      } else {
        radio.removeAttribute('checked');
      }
    });
  }

  @Watch('error')
  handleErrorChange(newVal) {
    if(newVal !== null) {
      Object.values(this.childrenData).map((child: HTMLElement) => {
        child.setAttribute('group-help-id', this.helpElementId);
      })
    }
  }

  @Listen('somChange')
  somChangeHandler(e) {
    this.value = e.detail.target.value;   
  }

  componentWillLoad() {
    this.helpElementId = `${this.groupId}-help`;

    // Let each radio button know that they are part of a group
    for (let i = 0; i < this.el.children.length; i++) {
      const child = this.el.children[i];
      child.setAttribute('has-group-parent', 'true');
      child.setAttribute('required-ind', 'none');
      (this.help || this.error) && child.setAttribute('group-help-id', this.helpElementId);

      if (this.required) {
        child.setAttribute('required', 'true');
      }
    }
  }

  componentDidLoad() {
    if (this.value) {
      this.handleValueChange(this.value);
    }

    this.childrenData = this.el.querySelector(`#${this.groupId}`).children;
  }

  render() {
    const _isInError = isInError(this.error);
    const helpMessage = _isInError ? this.error : this.help;
    const ariaInvalid = _isInError ? { 'aria-invalid': true } : {};

    const groupedContentClasses = ['grouped-content'];
    if (_isInError) {
      groupedContentClasses.push('is-invalid');
    }
    if (this.inline) {
      groupedContentClasses.push('grouped-content-inline');
    }

    return (
        <fieldset data-role="controlgroup" {...ariaInvalid}>        
          <legend aria-describedby={(this.help || this.error) ? this.helpElementId : null} class={`${this.labelClass} ${this.hideLabel ? 'sr-only' : ''}`}>
            <slot name="label">{this.label}</slot>
            {getRequiredInd(h, this.required, this.requiredInd)}
            { this.tooltip ?
              <span>
                <som-button class='tooltip-button' button-aria-label={`${this.label} information`} button-type="clear" icon-position="only" color="primary" icon="information" aria-describedby={this.tooltipID}>
                </som-button>
                <som-tooltip horizontal-position='auto' tooltip-id={this.tooltipID}>
                  {this.tooltip}
                </som-tooltip>
              </span> : null
            }
          </legend>

          <div id={this.groupId} class={groupedContentClasses.join(' ')}>
            <slot />
          </div>
          {helpMessage ? (
            <small id={this.helpElementId} class={`form-text ${_isInError ? 'text-error' : 'text-muted'}`}>
              {helpMessage}
            </small>
          ) : null}
        </fieldset>
    );
  }
}
